import React, { useCallback, useEffect, useRef } from 'react';
import {
	getActiveScreensaverUrl,
	getActiveSaverMeta,
} from '../../services/screensaverSettings';
import './AfterDarkScreensaver.css';

const ARROWS = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/**
 * Full-screen After Dark overlay. Dismiss on click, key, or pointer move
 * after a short grace period so the opening click does not instantly kill it.
 *
 * A module marked `interactive` (only Marbles (Tilt) so far) wants the pointer
 * and the keys for itself — its arrows swing the cow that steers gravity, and
 * she can be dragged. For those the host keeps its hands off: the iframe is
 * given focus and left clickable, and the module says when it is done by
 * posting `after-dark:stop`. This is the contract After Dark's own front page
 * uses; see `_tellHostToStop` in its marbles-tilt module.
 */
const AfterDarkScreensaver = ({ show, onHide }) => {
	const dismissRef = useRef(onHide);
	const armedRef = useRef(false);
	const frameRef = useRef(null);

	useEffect(() => {
		dismissRef.current = onHide;
	}, [onHide]);

	const dismiss = useCallback(() => {
		if (typeof dismissRef.current === 'function') dismissRef.current();
	}, []);

	// Read once per showing, so a settings change mid-saver can't switch the
	// input contract out from under the listeners below.
	const meta = show ? getActiveSaverMeta() : null;
	const interactive = Boolean(meta?.interactive);

	useEffect(() => {
		if (!show) {
			armedRef.current = false;
			return undefined;
		}
		armedRef.current = false;
		const armTimer = window.setTimeout(() => {
			armedRef.current = true;
		}, 400);

		// The module only gets its own keydown if the frame has focus — key
		// events in a cross-origin iframe never reach us, which is exactly
		// what we want for the arrows.
		if (interactive) frameRef.current?.focus?.();

		const onKey = (event) => {
			if (!armedRef.current) return;
			// Arrows belong to the module when it is one that steers.
			if (interactive && ARROWS.has(event.key)) return;
			event.preventDefault();
			dismiss();
		};
		const onPointer = () => {
			if (!armedRef.current) return;
			dismiss();
		};
		// Moving the mouse wakes it, as a screen saver should — but only past a
		// few pixels, so the jitter of a hand resting on the mouse (and the
		// stray move some browsers fire as the overlay appears under the
		// cursor) doesn't count.
		let origin = null;
		const onMove = (event) => {
			if (!armedRef.current) return;
			if (!origin) {
				origin = { x: event.clientX, y: event.clientY };
				return;
			}
			if (Math.abs(event.clientX - origin.x) + Math.abs(event.clientY - origin.y) < 12) return;
			dismiss();
		};
		// Only the frame we started gets to say when it is done.
		const onMessage = (event) => {
			if (event.data !== 'after-dark:stop') return;
			if (event.source && event.source !== frameRef.current?.contentWindow) return;
			dismiss();
		};

		window.addEventListener('keydown', onKey, true);
		window.addEventListener('message', onMessage);
		// An interactive module needs the pointer to steer with, and posts
		// `after-dark:stop` itself once the gesture isn't aimed at it.
		if (!interactive) {
			window.addEventListener('mousedown', onPointer, true);
			window.addEventListener('touchstart', onPointer, true);
			window.addEventListener('mousemove', onMove, true);
		}
		return () => {
			window.clearTimeout(armTimer);
			window.removeEventListener('keydown', onKey, true);
			window.removeEventListener('message', onMessage);
			window.removeEventListener('mousedown', onPointer, true);
			window.removeEventListener('touchstart', onPointer, true);
			window.removeEventListener('mousemove', onMove, true);
		};
	}, [show, interactive, dismiss]);

	if (!show) return null;

	const src = getActiveScreensaverUrl();

	return (
		<div
			className={`after-dark-screensaver${interactive ? ' is-interactive' : ''}`}
			role="dialog"
			aria-label={`${meta?.name || 'After Dark'} screen saver`}
			aria-modal="true"
		>
			<iframe
				ref={frameRef}
				className="after-dark-screensaver__frame"
				title={meta?.name || 'After Dark screen saver'}
				src={src}
				allow={meta?.allow || ''}
				scrolling="no"
				onLoad={() => { if (interactive) frameRef.current?.focus?.(); }}
			/>
			{/* The interactive module draws its own "drag the cow" hint, and
			    moving the mouse there is steering rather than dismissal — so
			    say click-or-key and don't promise a wiggle will do it. */}
			<div className="after-dark-screensaver__hint" aria-hidden="true">
				{interactive
					? 'Click, or press any other key, to dismiss'
					: 'Move the mouse, click, or press any key to dismiss'}
			</div>
		</div>
	);
};

export default AfterDarkScreensaver;
